import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError, apiGet, apiSend } from '../lib/api'
import type { Attempt, Task } from '../lib/types'

/**
 * «Что я тут решал» — журнал занятий по теме (§2.35).
 *
 * Ученик уходит с середины темы и возвращается через день: без журнала тема
 * выглядит нетронутой, а решённые задачи и разговор о них пропадают совсем.
 * Список строится по истории попыток в базе, поэтому переживает и перезагрузку,
 * и смену устройства; разговор сохранён только по последней задаче — про это
 * честно говорит подпись.
 *
 * Вход — кнопкой под задачей, как в боте (task_view.journal_row): лист темы
 * сразу выдаёт задачу, и экрана темы, где журнал жил раньше, ученик не видит
 * (M3-WEB-21).
 */
export default function History() {
  const { topicId = '' } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const { data, isLoading } = useQuery<{ items: Attempt[] }>({
    queryKey: ['history', topicId],
    queryFn: () => apiGet(`/api/tasks/history?node_id=${encodeURIComponent(topicId)}`),
  })

  // Последняя задача вернётся вместе с разговором о ней, задача постарше — с
  // чистого листа (полка диалога одна, см. engine/task_state.py).
  async function открыть(task_id: string) {
    setBusy(true)
    setError('')
    try {
      const task = await apiSend<Task>('/api/tasks/replay', 'POST', { task_id })
      queryClient.setQueryData(['task', 'current'], { task })
      navigate('/task')
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не получилось открыть задачу.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <h1>📜 Что я тут решал</h1>
      <p className="muted" style={{ margin: '0 0 12px' }}>
        Нажми на задачу, чтобы открыть её заново. Разбор сохранён у последней
        задачи — остальные откроются с чистого листа.
      </p>
      {error && <div className="error">{error}</div>}
      {isLoading && <div className="skeleton" />}
      {data && data.items.length === 0 && (
        <p className="muted">Здесь ты ещё ничего не решал(а). Возьми задачу — и она появится в журнале.</p>
      )}
      {data && data.items.length > 0 && (
        <div className="list">
          {data.items.map((item, i) => (
            <button
              key={`${item.task_id}-${i}`}
              className="list-item"
              disabled={busy}
              onClick={() => открыть(item.task_id)}
            >
              <span aria-hidden>{item.is_correct ? '✅' : '❌'}</span>
              <span className="title">
                {item.preview}
                <span className="small muted" style={{ display: 'block' }}>
                  {дата(item.created_at)}
                  {item.max_score != null && item.score != null
                    ? ` · ${item.score} из ${item.max_score} б`
                    : ''}
                  {item.has_dialogue ? ' · с разбором' : ''}
                </span>
              </span>
              <span className="meta">↩️</span>
            </button>
          ))}
        </div>
      )}
      <div className="list list-grid" style={{ marginTop: 8 }}>
        <button className="list-item" onClick={() => navigate(-1)}>
          <span className="title">⬅️ К задаче</span>
        </button>
        <button className="list-item" onClick={() => navigate('/')}>
          <span className="title">🏠 Главная</span>
        </button>
      </div>
    </>
  )
}

function дата(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })
}
